import { ref, computed, watch, onMounted, onScopeDispose, type Ref } from 'vue'

interface TypewriterOptions {
  /** Baseline characters revealed per second. */
  charsPerSecond?: number
  /** When false the full source is shown at once (history, reduced motion). */
  enabled?: boolean
}

/**
 * Reveals `source` one character at a time so a streamed bot reply reads like it
 * is being typed live. The backend delivers the answer in bursts; this decouples
 * display speed from arrival so the reveal stays smooth, while a backlog-based
 * catch-up keeps it from ever lagging far behind the text already received.
 *
 * `active` is the message's streaming flag — while it is true more text may
 * still arrive; once it is false the remainder is revealed and typing ends.
 */
export function useTypewriter(
  source: Ref<string>,
  active: Ref<boolean>,
  options: TypewriterOptions = {},
) {
  const cps = options.charsPerSecond ?? 50
  const enabled = options.enabled ?? true
  const displayed = ref(enabled ? '' : source.value)

  let frame = 0
  let prevTime = 0
  let carry = 0

  const caughtUp = () => displayed.value.length >= source.value.length

  function schedule() {
    if (frame || typeof requestAnimationFrame === 'undefined') return
    frame = requestAnimationFrame(step)
  }

  function step(now: number) {
    frame = 0
    if (!prevTime) prevTime = now
    const dt = (now - prevTime) / 1000
    prevTime = now

    const remaining = source.value.length - displayed.value.length
    if (remaining > 0) {
      carry += dt * cps
      let reveal = Math.floor(carry)
      carry -= reveal
      // Speed up with the backlog so a large chunk doesn't crawl in slowly.
      reveal += Math.floor(remaining / 12)
      reveal = Math.max(1, Math.min(reveal, remaining))
      displayed.value = source.value.slice(0, displayed.value.length + reveal)
    }

    // Stop when we've shown everything received; the source watcher restarts the
    // loop when the next chunk lands, so we never idle-spin waiting for it.
    if (!caughtUp()) schedule()
    else prevTime = 0
  }

  function stop() {
    if (frame) cancelAnimationFrame(frame)
    frame = 0
    prevTime = 0
  }

  onMounted(() => {
    if (enabled) schedule()
    else displayed.value = source.value
  })

  watch([source, active], () => {
    if (!enabled) {
      displayed.value = source.value
      return
    }
    // Defensive: if the source ever shrinks, resync rather than show stale text.
    if (source.value.length < displayed.value.length) displayed.value = source.value
    if (!caughtUp()) schedule()
  })

  onScopeDispose(stop)

  const done = computed(() => !active.value && caughtUp())

  return { displayed, done }
}
